import { Link } from 'react-router-dom';
import {
  Disc3,
  Sparkles,
  BadgeCheck,
  AlertTriangle,
  Music,
  Layers,
  Headphones,
  ListPlus,
  Smartphone,
  Video,
  Phone,
  ShieldCheck,
  Check,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import BrandWordmark from '@/components/BrandWordmark';
import { HowItWorksGrid } from '@/components/HowItWorks';

/**
 * Marketing Landing page — shown to unauthenticated visitors at `/`.
 *
 * Order (top → bottom):
 *   1. Header
 *   2. Hero (with mini 4-swatch collection mock + "keep scrolling" teaser)
 *   3. Why GroovLens — emotional positioning (solid bg-card inner, no
 *      gradient bleed; the thin animated ring is the only color motion)
 *   4. Headline features — 6-card grid of marquee attractions
 *   5. How it works — Snap / Recognize / Act
 *   6. Final CTA
 *   7. Footer
 *
 * Every major content panel uses `.crate-neon-border` (Pattern A — same
 * recipe as the music card detail modal): a 2px-padded wrapper with an
 * animated linear-gradient background, holding an inner solid `bg-card`
 * div that occludes everything except the 2px ring. Thin and clean —
 * not chrome-y. Bulletproof on iOS Safari, no @property animations, no
 * mask-composite trickery.
 *
 * For everyone (not just DJs): copy intentionally names "music lovers,
 * listeners, vinyl heads, playlist curators, DJs, casual fans" as the
 * audience. The 33 subgenre showcase leans on mainstream genres FIRST
 * (Rock, Hip-Hop, R&B, Jazz...) and then mentions the specialty
 * electronic subgenres so non-DJ visitors see themselves represented.
 */
export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      {/* Decorative gradient at the very top (kept under 20% viewport per
          design guidelines). Adds a teal glow above the hero. */}
      <div
        className="absolute inset-x-0 top-0 h-[55vh] pointer-events-none bg-crate-glow noise"
        aria-hidden="true"
      />

      {/* ---------- Header ---------- */}
      <header className="relative z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <BrandWordmark size="lg" />
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" data-testid="landing-login-link" className="rounded-lg">
                Log in
              </Button>
            </Link>
            <Link to="/signup">
              <Button data-testid="landing-signup-link" className="rounded-lg">
                Create account
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="relative z-10">
        {/* ---------- Hero ----------
            Tight bottom padding so "Why GroovLens" sits close to the
            hero without a yawning gap. 8/4 split gives the copy room
            to breathe.

            First-load choreography: the hero fades + scales in on
            mount with the same 100ms ease-out spec as every modal in
            the app, so entering GroovLens feels intentional from the
            very first frame. `motion-reduce:animate-none` respects
            `prefers-reduced-motion` in lockstep with the modal system. */}
        <section
          className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10 pb-2 grid lg:grid-cols-12 gap-6 lg:gap-8 items-start animate-in fade-in zoom-in-95 duration-100 fill-mode-both motion-reduce:animate-none"
          data-testid="landing-hero"
        >
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border bg-card text-xs text-muted-foreground mb-5">
              <Sparkles className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
              AI-powered music organizer · Rock, Hip-Hop, Jazz, Electronic and beyond
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              Turn music snapshots into a crate worth digging.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl">
              Drop your Spotify, Shazam, Apple Music and Instagram snapshots. GroovLens reads every
              pixel and sorts them into <span className="text-foreground font-medium">33 sharp
              genre crates</span> — from Rock, Hip-Hop, R&amp;B and Jazz to Deep House, Tech House and
              Liquid DnB — and surfaces buy + preview links, BPM, key and artist deep dives for
              every pull.{' '}
              <span className="text-foreground/80 font-medium">Keep scrolling to find out how it works.</span>
            </p>

            {/* CTAs */}
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/signup">
                <Button size="lg" data-testid="landing-cta-primary" className="rounded-xl">
                  Start digging <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="secondary" data-testid="landing-cta-secondary" className="rounded-xl">
                  I have an account
                </Button>
              </Link>
            </div>

            {/* Trust badges */}
            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-[hsl(var(--primary))]" /> 7-day free trial
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-[hsl(var(--primary))]" /> No credit card required
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 text-[hsl(var(--primary))]" /> Sign in with Google, Spotify, Discord or X
              </span>
            </div>
          </div>

          {/* Compact visual mock — `max-w-sm` cap is a touch larger than xs.
              4 swatches in a 2×2 grid reads cleaner than the 6-tile 3×2
              variant we briefly tried. */}
          <div className="lg:col-span-4">
            <div className="relative rounded-2xl border border-border bg-card overflow-hidden noise max-w-sm mx-auto lg:ml-auto lg:mr-0">
              <div className="p-3 grid grid-cols-2 gap-2">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={`mock-tile-${i}`}
                    className="aspect-square rounded-md border border-border"
                    style={{
                      backgroundImage: [
                        'linear-gradient(135deg,#18C7B8,#0e7a73)',  // teal — House
                        'linear-gradient(135deg,#FFB24A,#8b5e29)',  // amber — Jazz
                        'linear-gradient(135deg,#FF6B6B,#b03e3e)',  // coral — Hip-Hop
                        'linear-gradient(135deg,#3FC9FF,#155f80)',  // sky — R&B
                      ][i - 1],
                    }}
                  />
                ))}
              </div>
              <div className="px-3 py-2.5 border-t border-border flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-display font-semibold text-sm truncate">Your Collection</div>
                  <div className="text-[10px] text-muted-foreground">
                    142 pulls · 4 genre crates
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-md bg-[var(--brand-lime)]/10 text-[var(--brand-lime)] border border-[var(--brand-lime)]/30">
                    <BadgeCheck className="w-2.5 h-2.5" /> 24
                  </span>
                  <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-md bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border border-[hsl(var(--destructive))]/30">
                    <AlertTriangle className="w-2.5 h-2.5" /> 3
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ---------- Why GroovLens (positioning) ----------
            Sits first so visitors agree with the problem before we walk
            them through features and steps. Inner is SOLID `bg-card` —
            no internal gradient bleed; the only visible color movement
            is the thin animated border ring. */}
        <section
          className="max-w-6xl mx-auto px-4 sm:px-6 pt-2 pb-6 sm:pb-8"
          data-testid="landing-why"
        >
          <NeonBorderPanel innerClassName="bg-card p-8 sm:p-12 noise rounded-[14px]">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-xs font-mono uppercase tracking-wider text-[hsl(var(--primary))] mb-2">
                  Why GroovLens
                </div>
                <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                  Everyone has 2,000 snapshots of songs they never opened again.
                </h2>
                <p className="text-muted-foreground mt-4 text-base sm:text-lg">
                  Stop losing tracks in your camera roll. GroovLens turns those forgotten
                  snapshots into a working crate — searchable, sorted, sellable, playable.
                  Works for every kind of music collector — listeners, DJs, vinyl heads,
                  playlist curators and casual fans alike.
                </p>
                <p className="text-sm text-muted-foreground mt-4">
                  Built by{' '}
                  <span className="text-foreground font-medium">Soundstixx</span>{' '}
                  at <span className="text-foreground font-medium">Beatseek</span> — for music
                  lovers, by a music lover.
                </p>
              </div>
              <ul className="grid grid-cols-1 gap-3">
                <Stat icon={Disc3} label="Snapshots → organized crates" value="In under 60 seconds" />
                <Stat icon={Layers} label="Subgenres available" value="33 and growing" />
                <Stat icon={Music} label="Stores supported per track" value="5+ (Beatport, Traxsource, Bandcamp, iTunes, Amazon)" />
                <Stat icon={ShieldCheck} label="Your data" value="Private to your account" />
              </ul>
            </div>
          </NeonBorderPanel>
        </section>

        {/* ---------- Feature grid (major attractions only) ----------
            Sits BEFORE "How it works" so visitors see the headline
            attractions immediately after the Why panel, then learn
            the workflow. */}
        <section
          className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8"
          data-testid="landing-features"
        >
          <div className="text-center max-w-3xl mx-auto mb-8">
            <div className="text-xs font-mono uppercase tracking-wider text-[hsl(var(--primary))] mb-2">
              The headline features
            </div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight lg:whitespace-nowrap">
              Built for the way people actually find music
            </h2>
            <p className="text-muted-foreground mt-3">
              Every feature is shaped by real workflow: snapshot first, organize later,
              buy when ready, play when needed.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <FeatureCard
              icon={Layers}
              accent="primary"
              title="33 sharp subgenres"
              copy="Rock, Hip-Hop, Jazz, R&amp;B, Soul, Country, Metal, Latin, Pop, Reggae — plus specialty electronic genres like Deep House, Tech House, Liquid DnB, Neurofunk, Afro House and Minimal Techno. Three databases have to agree before a track moves."
            />
            <FeatureCard
              icon={Headphones}
              accent="lime"
              title="Artist + track deep dives"
              copy="Tap any pull — see top tracks, related artists, full bio, Spotify links, BPM, key, energy and danceability. Find your next obsession in two taps."
            />
            <FeatureCard
              icon={ListPlus}
              accent="amber"
              title="Spotify auto-connect"
              copy="Sign in with Spotify and your account links automatically. Push an entire crate to a new or existing Spotify playlist in seconds — no extra setup."
            />
            <FeatureCard
              icon={Smartphone}
              accent="coral"
              title="App experience, no app store"
              copy="Install GroovLens straight to your home screen on iPhone, Android, or desktop. Full-screen, fast, offline-friendly. The app you want without the App Store gatekeeping."
            />
            <FeatureCard
              icon={Video}
              accent="primary"
              title="Music video lookup"
              copy="Every track is auto-matched to its official music video on YouTube. One tap from snapshot to full music video — free."
            />
            <FeatureCard
              icon={Phone}
              accent="lime"
              title="Ringtone match"
              copy="Want a track as your ringtone? GroovLens links every pull straight to its iTunes ringtone purchase — your favorite drop on every incoming call."
            />
          </div>
        </section>

        {/* ---------- How it works (3 steps) ----------
            Sits AFTER features so visitors who got hooked on the
            attractions can now see the actual workflow. */}
        <section
          className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8"
          data-testid="landing-how-it-works"
        >
          <div className="text-center max-w-2xl mx-auto mb-8">
            <div className="text-xs font-mono uppercase tracking-wider text-[hsl(var(--primary))] mb-2">
              How it works
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Three taps from snapshot to crate
            </h2>
            <p className="text-muted-foreground mt-3">
              Stop scrolling through camera-roll graveyards of tracks you swore you&apos;d come back to.
              GroovLens does the boring part so you can get back to digging.
            </p>
          </div>

          <HowItWorksGrid />
        </section>

        {/* ---------- Final CTA ---------- */}
        <section
          className="max-w-6xl mx-auto px-4 sm:px-6 pt-2 pb-10 sm:pb-12"
          data-testid="landing-final-cta"
        >
          <div className="rounded-2xl border border-border bg-card p-10 text-center">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Drop your first snapshot in 60 seconds.
            </h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
              No credit card. No app store. Just sign up, snap, and watch your crate fill itself.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link to="/signup">
                <Button size="lg" className="rounded-xl" data-testid="landing-final-cta-button">
                  Create your account <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="ghost" className="rounded-xl" data-testid="landing-final-login-button">
                  Already on GroovLens? Log in
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* ---------- Footer ---------- */}
      <footer className="relative z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <span>Powered by</span>
            <FooterLink href="https://getsongbpm.com">GetSongBPM</FooterLink>
            <span aria-hidden="true">·</span>
            <FooterLink href="https://musicbrainz.org">MusicBrainz</FooterLink>
            <span aria-hidden="true">·</span>
            <FooterLink href="https://www.last.fm">Last.fm</FooterLink>
            <span aria-hidden="true">·</span>
            <FooterLink href="https://www.theaudiodb.com">TheAudioDB</FooterLink>
            <span aria-hidden="true">·</span>
            <FooterLink href="https://open.spotify.com">Spotify</FooterLink>
            <span aria-hidden="true">·</span>
            <FooterLink href="https://music.apple.com">Apple Music</FooterLink>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
            <span>© {new Date().getFullYear()} GroovLens · by Beatseek · Built for music lovers</span>
            <span className="font-mono">v0.4 · Subgenres + Spotify SSO + Username login</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ============================================================
 * Presentational helpers
 * ============================================================ */

/**
 * NeonBorderPanel — wraps content in the same animated gradient border used
 * on the music card detail modal (".crate-neon-border" Pattern A). The
 * outer div has a 6px-padded animated linear gradient as its background;
 * the inner div sits on top with `bg-card` (or whatever inner styling you
 * pass), occluding everything except the 6px ring. Bulletproof on iOS
 * Safari — no @property animations, no mask-composite trickery.
 *
 * Default `innerClassName` covers the common case (solid card + 10px
 * inner radius which is 16px outer minus the 6px ring).
 */
function NeonBorderPanel({ children, innerClassName = 'bg-card rounded-[14px] p-6 h-full' }) {
  return (
    <div className="crate-neon-border rounded-2xl h-full">
      <div className={innerClassName}>{children}</div>
    </div>
  );
}

function FeatureCard({ icon: Icon, accent, title, copy }) {
  const accentMap = {
    primary: 'text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10',
    lime: 'text-[var(--brand-lime)] bg-[var(--brand-lime)]/10',
    amber: 'text-[var(--brand-amber)] bg-[var(--brand-amber)]/10',
    coral: 'text-[var(--brand-coral)] bg-[var(--brand-coral)]/10',
  };
  return (
    <NeonBorderPanel innerClassName="bg-card rounded-[14px] p-5 h-full">
      <div className={`w-10 h-10 rounded-xl grid place-items-center mb-3 ${accentMap[accent] || accentMap.primary}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="font-display font-semibold">{title}</div>
      <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{copy}</p>
    </NeonBorderPanel>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <li className="rounded-xl border border-border bg-background/50 p-4 flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] grid place-items-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="font-display font-semibold leading-snug">{value}</div>
      </div>
    </li>
  );
}

function FooterLink({ href, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="hover:text-[hsl(var(--primary))] transition-colors"
    >
      {children}
    </a>
  );
}
